<template>
  <div id="sticky-show" class="pm row">
    <!-- Mobile Top -->
    <div class="col-12 pm__top is--mobile">
      <ImageField v-if="product.second_turn_logo" class="pm__secondTurn-logo" :obj="{'url': product.second_turn_logo,'alt':'Second Turn Logo','width':112,'height':23}"/>
      <h3 class="pm__title">{{ product.title }}</h3>
      <a data-stio-gtm-id="PDP: Reviews Top" v-if="product.id && !isSecondTurn" class="pm__review-wrapper" href="#reviews" :aria-roledescription="`Reviews link of product ${product.title}`"> 
        <div class="yotpo-widget-instance" data-yotpo-instance-id="498880" :data-yotpo-product-id="product.id"></div>
      </a>
    </div>

    <!-- Product Images -->
    <ProductImages
      :product="product"
      :productForm="productForm"
    />

    <!-- BuyBox Code -->
    <div class="col-12 col-tl-5 pm__buyBox" id="pm-buyBox">
      <!-- Price Module Mobile -->
      <div class="pm__price-wrapper-mobile">
        <ProductPrice
          :product="product"
          :selectedFirstOption="selectedFirstLevelOption"
        />
      </div>

      <!-- Desktop Top Information -->
      <div class="pm__top-desktop is--desktop">
        <ImageField v-if="product.second_turn_logo" class="pm__secondTurn-logo" :obj="{'url': product.second_turn_logo,'alt':'Second Turn Logo','width':136,'height':28}"/>
        <h3 class="pm__title">{{ product.title }}</h3>
        <!-- Price and review wrapper desktop -->
        <div class="pm__price-review">
          <ProductPrice
            :product="product"
            :selectedFirstOption="selectedFirstLevelOption"
          />
          <a data-stio-gtm-id="PDP: Reviews Top" v-if="product.id && !isSecondTurn" class="pm__review-wrapper" href="#reviews" :aria-roledescription="`Reviews link of product ${product.title}`"> 
            <div class="yotpo-widget-instance" data-yotpo-instance-id="498880" :data-yotpo-product-id="product.id"></div>
          </a>
        </div>
      </div>

      <!-- Product Options -->
      <ProductOptions
        :product="product"
        :selectedOptions="productForm"
        :isSecondTurn="isSecondTurn"
        @on-option-change="onOptionChange"
      />

      <!-- Size Guide Btn -->
      <button v-if="sizeGuideVal && !isSecondTurn" class="pm__size-guide" @click="toggleSizeDrawer" tabindex="0">Size Guide</button>
     
      <!-- Product Stock Info -->
      <ProductStockInfo
        v-if="!isATCDisabled"
        :product="product"
        :selectedVariant="productForm.selectedVariant"
        :selectedFirstOption="selectedFirstLevelOption"
      />
      <div v-else class="pm__stock-info">
        <div class="pm__inventory-out-of-stock-msg">
          The last in-stock item for your selection is already in your cart. Check out now before it's gone for good!
        </div>
      </div>

      <!-- Add to cart btn -->
      <div data-stio-gtm-id="PDP: ATC CTA">
        <ProductATC
          :product="product"
          :productForm="productForm"
          :selectedVariant="productForm.selectedVariant"
          :selectedFirstOption="selectedFirstLevelOption"
          @on-atc-state-change="onATCStateChange"
        />
      </div>

      <div class="pm__paymethods" v-if="!product.title.includes('E-Gift Card')">
        <span class="pm__paymethods-inline-container">
          <span v-if="productForm?.selectedVariant?.price > 3500 || product?.price > 3500" class="pm__paymethods-text">{{ totalEMI }} payments of {{ amountEMI }} with</span>
            <svg class="afterpay-image" role="img" aria-label="Afterpay image">
              <use href="#afterpay-logo"></use>
            </svg>
          <span class="pm__paymethods-separator">or</span>
          <KlarnaLogo role="img" aria-label="Klarna image"/>
          <span v-if="productForm?.selectedVariant?.price <= 3500 || product?.price <= 3500" class="pm__paymethods-text">available for orders over $35</span>
          <TooltipModal>
            <template #tooltipContent>
              <div class="tooltip-content" role="tooltip">
                <p>Buy items now and pay later - in {{ totalEMI }} payments, with no additional fees.</p>
                <a :href="generateBNPLpageURL()" target="_blank">Learn more</a>
              </div>
            </template>
            <button aria-label="Tooltip" :aria-roledescription="`Tooltip button for ${totalEMI} payments of ${amountEMI} with afterpay or klarna`">
              <svg class="pm__paymethods-tooltip" role="img" aria-label="Tooltip image" aria-hidden="true">
                <use href="#tooltip-icon"></use>
              </svg>
            </button>
          </TooltipModal>
        </span>
      </div>

      <!-- Callouts -->
      <div class="pm__callouts-second-turn" v-if="isSecondTurn">
        <ImageField
          class="pm__callouts-second-turn-icon"
          :obj="{'url': product.callout_second_turn.icon,'alt':product.callout_second_turn.title,'width':40,'height':40}"
          :load="true"
        />
        <div class="pm__callouts-second-turn-content" v-html="product.callout_second_turn.content"></div>
      </div>
      <div class="pm__callouts-test" v-else>
        <ImageField
          class="pm__callout-icon"
          :obj="{'url': product.callout_test.icon,'alt':product.callout_test.title,'width':30,'height':40}"
          :load="true"
        />
        <p class="pm__callouts-test-strong">
          <strong>{{ product.callout_test.title }}</strong>
        </p>
        <div class="pm__callouts-test-content" v-html="product.callout_test.content"></div>
      </div>

      <!-- Additional info component -->
      <div class="pm__additional-info" v-if="product.product_info.length === 1" v-html="product.product_info[0].content"></div>
      <div class="pm__additional-info" v-else-if="product.product_info.length > 1 && productForm.Color">
        <span v-for="(data,index) in product.product_info" :key="index">
          <span v-show="data.color.toLowerCase() === productForm.Color.toLowerCase()" v-html="data.content"></span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeMount, watch } from 'vue';
  import {useStore} from "vuex";
  import ImageField from '../../global/image.vue';
  import ProductImages from './product-images.vue';
  import ProductPrice from './product-price.vue';
  import ProductOptions from './product-options/index.vue';
  import ProductStockInfo from './product-stock-info.vue';
  import ProductATC from './product-atc.vue';
  import TooltipModal from '../../global/tooltip-modal.vue';
  import KlarnaLogo from './klarna-image.vue'; // need to check with soha for svg

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({})
    }
  })

  const store = useStore();
  const product = reactive(props.product)
  const firstOption = reactive(product.options[0].name)
  const productForm = reactive(store.state.pdp.productForm)
  const sizeGuideVal = ref(null)
  const isATCDisabled = ref(false);
  const totalEMI = 4;

  const selectedFirstLevelOption = computed(() => store.state.pdp.firstLevelOptions?.[product.id]);

  onMounted(async() => {
    initialSetup();

    // Size Guide Data Fetch
    sizeGuideVal.value = await(await fetch(`${window.location.pathname}?view=size-guide`)).json()

    document.querySelectorAll('.sizeDrawerOpen').forEach((el) => {
      el.addEventListener('click', () => {
        toggleSizeDrawer()
      })
    })
  })

  const isSecondTurn = computed(() => {
    return product.tags.includes('Second Turn')
  })

  const amountEMI = computed(() => {
    const price = productForm?.selectedVariant?.price || product?.price;
    return window.formattedPrice((price/totalEMI)/100);
  });

  const additionalPropertyOptions = computed(() => {
    return product.additional_property_options != '' ? product.additional_property_options.split(',') : []
  })

  const onOptionChange = (key,value,index,length) => {
    updateProductForm(key,value)
    updateVariantValue();

    // if(key != 'Color' && key != 'Title'){
    //   updateColorOOS(value,index,length)
    // }
  }

  const onATCStateChange = (value) => {
    isATCDisabled.value = value;
  }

  const updateVariantValue = () => {
    const { options } = product;
    const variantValue = options.map(option => productForm[option.name]).join(' / ');
    updateSelectedVariant(variantValue);
  };

  const updateSelectedVariant = (value) => {   
    const variant = product.variants.find(item => item.value === value);
    const selectedVariant = variant || null;
    updateProductForm('selectedVariant', selectedVariant);
    updateParams('update', productForm.selectedVariant);
  };

  const initialSetup = () => {
    const { options, variants } = product;
    var params = new window.URLSearchParams(window.location.search);
    var variantParam = params.get("variant")
    var colorParam = params.get("color")

    // Delete unnecessary parameters from the URL
    updateParams('delete')

    if (isSecondTurn.value || variantParam || options.length === 1) {
      // Find the selectedVariant or use the first variant
      const selectedVariant = variantParam
        ? variants.find(variant => variant.id === variantParam)
        : variants[0];

      // Set the selectedVariant
      updateProductForm('selectedVariant', selectedVariant)

      // Set options based on the selectedVariant
      options.forEach((option, index) => {
        updateProductForm(option.name, selectedVariant[`option${index + 1}`])
      });

      updateParams('update', selectedVariant)
    } else {
      if (colorParam) {
        updateProductForm('Color', colorParam)
      } else {
        watchSelectedFirstLevelOptionOnceReady();
      }

      // For Inseam and Outseam 
      if (additionalPropertyOptions.value.length) {
        const ioData = options.find(ele => ele.name === 'Inseam' || ele.name === 'Outseam')
        ioData && updateProductForm(ioData['name'], ioData['values'][0])
      }

      updateProductForm('selectedVariant', null)
    }
  }

  const updateParams = (type, variant) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (type === 'update' && variant) {
      params.set('variant', variant.id);
    } else if (type === 'delete') {
      params.delete('color');
    }

    url.search = params.toString();
    window.history.replaceState({ url: url.toString() }, null, url.toString());
  };

  const updateProductForm = (key,value) => {
    store.dispatch("updatePDPProductForm",{
      key: key,
      value: value
    });
  }

  const toggleSizeDrawer = () => {
    window.toggleDrawer(true,'SizeGuide',
      {
        'sizeguide':sizeGuideVal.value,
        'fitType': product.fit_type
      }
    )
  }

  const generateBNPLpageURL = () => {
    return window.location.origin + '/pages/bnpl';
  };

  const watchSelectedFirstLevelOptionOnceReady = () => {
    const unwatch = watch(selectedFirstLevelOption, (newVal) => {
      if (newVal?.value) {
        updateProductForm(firstOption, newVal.value);
        unwatch(); // Stop watching after the first execution
      }
    });
  };
</script>